"use client";
import {Tab, Tabs} from "@nextui-org/react";
import {NextPage} from "next";
import type {ReactNode} from "react";
import {useMemo, useState} from "react";
import {FiAlertTriangle, FiCheckCircle, FiSlash} from "react-icons/fi";
import {Active} from "./active";
import {FalseAlarm} from "./falseAlarm";
import {Resolved} from "./resolved";
import {SosStatusChart} from "./sos-status-chart";

type SosTabStatus = 1 | 2 | 3;

type SosUser = {
  id: string;
  name: string;
  bookingId: string;
  avatar: string;
  phone: string;
  boardingPoint: string;
  dropPoint: string;
  reason: string;
  travelFrom: string;
  travelTo: string;
  travelDate: string;
  totalTravelTime: string;
};

type SosCardItem = {
  id: string;
  busNo: string;
  pickupTime: string;
  caretakerName: string;
  driverCode: string;
  driverPh: string;
  from: string;
  to: string;
  status: SosTabStatus;
  users: SosUser[];
};

type SosFlatRow = {
  id: string;
  name: string;
  bookingId: string;
  avatar: string;
  pickupTime: string;
  caretakerName: string;
  driverCode: string;
  from: string;
  to: string;
  status: SosTabStatus;
  userPh: string;
  driverPh: string;
  busNo: string;
  reason: string;
  travelFrom: string;
  travelTo: string;
  travelDate: string;
  totalTravelTime: string;
};

const sosRows: SosFlatRow[] = [
  {
    id: "bk_1123",
    name: "Sarah Jenkins",
    bookingId: "1123",
    avatar: "/images/avatar.png",
    pickupTime: "10:30 AM",
    caretakerName: "Ravikumar",
    driverCode: "DRV10123",
    from: "Tidel Park, Coimbatore, Tamilnadu",
    to: "Sulur, Coimbatore, Tamilnadu",
    status: 2,
    userPh: "1234567899",
    driverPh: "0987654321",
    busNo: "TN38 DE 2312",
    reason: "Emergency support requested by rider",
    travelFrom: "10:32 AM",
    travelTo: "11:06 AM",
    travelDate: "2026-05-07",
    totalTravelTime: "34 mins"
  },
  {
    id: "bk_1124",
    name: "Arun Kumar",
    bookingId: "1124",
    avatar: "/images/avatar.png",
    pickupTime: "08:15 AM",
    caretakerName: "Meena",
    driverCode: "DRV1029",
    from: "Gandhipuram, Coimbatore, Tamilnadu",
    to: "Peelamedu, Coimbatore, Tamilnadu",
    status: 1,
    userPh: "1234567899",
    driverPh: "0987654321",
    busNo: "TN38 DE 2312",
    reason: "SOS button pressed during travel",
    travelFrom: "08:18 AM",
    travelTo: "08:47 AM",
    travelDate: "2026-05-07",
    totalTravelTime: "29 mins"
  },
  {
    id: "bk_1125",
    name: "Priya Sharma",
    bookingId: "1125",
    avatar: "/images/avatar.png",
    pickupTime: "05:45 PM",
    caretakerName: "Suresh",
    driverCode: "DRV7781",
    from: "Saravanampatti, Coimbatore, Tamilnadu",
    to: "Ukkadam, Coimbatore, Tamilnadu",
    status: 2,
    userPh: "1234567888",
    driverPh: "0987654321",
    busNo: "TN38 DE 2312",
    reason: "Late-night drop safety concern",
    travelFrom: "05:48 PM",
    travelTo: "06:22 PM",
    travelDate: "2026-05-08",
    totalTravelTime: "34 mins"
  },
  {
    id: "bk_1126",
    name: "Karthik Raj",
    bookingId: "1126",
    avatar: "/images/avatar.png",
    pickupTime: "07:10 AM",
    caretakerName: "Divya",
    driverCode: "DRV3304",
    from: "RS Puram, Coimbatore, Tamilnadu",
    to: "Singanallur, Coimbatore, Tamilnadu",
    status: 1,
    userPh: "1234567877",
    driverPh: "0987654322",
    busNo: "TN38 DE 2312",
    reason: "Route deviation reported",
    travelFrom: "07:12 AM",
    travelTo: "07:43 AM",
    travelDate: "2026-05-08",
    totalTravelTime: "31 mins"
  },
  {
    id: "bk_1127",
    name: "Nivetha",
    bookingId: "1127",
    avatar: "/images/avatar.png",
    pickupTime: "09:00 AM",
    caretakerName: "Harish",
    driverCode: "DRV5568",
    from: "Saibaba Colony, Coimbatore, Tamilnadu",
    to: "Hope College, Coimbatore, Tamilnadu",
    status: 3,
    userPh: "1234567866",
    driverPh: "0987654323",
    busNo: "TN38 DE 2312",
    reason: "False alarm after user callback",
    travelFrom: "09:02 AM",
    travelTo: "09:25 AM",
    travelDate: "2026-05-09",
    totalTravelTime: "23 mins"
  },
  {
    id: "bk_1128",
    name: "Vignesh",
    bookingId: "1128",
    avatar: "/images/avatar.png",
    pickupTime: "06:50 PM",
    caretakerName: "Lakshmi",
    driverCode: "DRV9087",
    from: "Podanur, Coimbatore, Tamilnadu",
    to: "Town Hall, Coimbatore, Tamilnadu",
    status: 2,
    userPh: "1234567855",
    driverPh: "0987654324",
    busNo: "TN66 BY 2295",
    reason: "Missed stop escalation",
    travelFrom: "06:53 PM",
    travelTo: "07:24 PM",
    travelDate: "2026-05-09",
    totalTravelTime: "31 mins"
  },
  {
    id: "bk_1129",
    name: "Deepika",
    bookingId: "1129",
    avatar: "/images/avatar.png",
    pickupTime: "11:20 AM",
    caretakerName: "Prabhu",
    driverCode: "DRV4412",
    from: "Vilankurichi, Coimbatore, Tamilnadu",
    to: "Ondipudur, Coimbatore, Tamilnadu",
    status: 3,
    userPh: "1234567844",
    driverPh: "0987654325",
    busNo: "TN66 BY 2295",
    reason: "Accidental trigger confirmed",
    travelFrom: "11:23 AM",
    travelTo: "11:48 AM",
    travelDate: "2026-05-10",
    totalTravelTime: "25 mins"
  },
  {
    id: "bk_1130",
    name: "Rohit",
    bookingId: "1130",
    avatar: "/images/avatar.png",
    pickupTime: "01:10 PM",
    caretakerName: "Anitha",
    driverCode: "DRV6621",
    from: "Kalapatti, Coimbatore, Tamilnadu",
    to: "Avinashi Road, Coimbatore, Tamilnadu",
    status: 1,
    userPh: "1234567833",
    driverPh: "0987654326",
    busNo: "TN66 BY 2295",
    reason: "Driver unreachable for pickup",
    travelFrom: "01:12 PM",
    travelTo: "01:46 PM",
    travelDate: "2026-05-10",
    totalTravelTime: "34 mins"
  }
];

const SosStatCard = ({
  label,
  value,
  helper,
  icon,
  iconClass,
  valueClass,
  borderClass = "border-slate-200"
}: {
  label: string;
  value: number;
  helper?: string;
  icon: ReactNode;
  iconClass: string;
  valueClass: string;
  borderClass?: string;
}) => (
  <div
    className={`h-40 w-[220px] rounded-[22px] border bg-white px-4 py-3 shadow-sm ${borderClass}`}
  >
    <div className="flex items-start gap-3">
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-2xl ${iconClass}`}
      >
        {icon}
      </div>
    </div>
    <div className="mt-2.5">
      <div className="text-sm font-semibold text-slate-800">{label}</div>
      <div className={`mt-1 text-3xl font-bold tracking-tight ${valueClass}`}>
        {value}
      </div>
      {helper ? (
        <div className="mt-0.5 text-xs text-slate-500">{helper}</div>
      ) : null}
    </div>
  </div>
);

const buildInitialCards = (rows: SosFlatRow[]): SosCardItem[] => {
  const grouped = new Map<string, SosCardItem>();

  rows.forEach(row => {
    const key = `${row.status}-${row.busNo}`;

    if (!grouped.has(key)) {
      grouped.set(key, {
        id: `card-${row.status}-${row.busNo.replace(/\s+/g, "-")}`,
        busNo: row.busNo,
        pickupTime: row.pickupTime,
        caretakerName: row.caretakerName,
        driverCode: row.driverCode,
        driverPh: row.driverPh,
        from: row.from,
        to: row.to,
        status: row.status,
        users: []
      });
    }

    grouped.get(key)!.users.push({
      id: row.id,
      name: row.name,
      bookingId: row.bookingId,
      avatar: row.avatar,
      phone: row.userPh,
      boardingPoint: row.from,
      dropPoint: row.to,
      reason: row.reason,
      travelFrom: row.travelFrom,
      travelTo: row.travelTo,
      travelDate: row.travelDate,
      totalTravelTime: row.totalTravelTime
    });
  });

  return Array.from(grouped.values());
};

export const SOS: NextPage<{
  hasDateFilterChanged: boolean;
  searchValue: string;
  filterRequest: {
    from: number | null;
    to: number | null;
  };
}> = function ({hasDateFilterChanged, searchValue, filterRequest}) {
  const [activeTab, setActiveTab] = useState("active");
  const [cards, setCards] = useState<SosCardItem[]>(buildInitialCards(sosRows));
  const [tabs, setTabs] = useState([
    {id: "active", label: "Active"},
    {id: "resolved", label: "Resolved"},
    {id: "falseAlarm", label: "False Alarm"}
  ]);

  const updateTabLabel = (selectedTab: string) => {
    setTabs(prevTabs =>
      prevTabs.map(tab => {
        if (tab.id === "active") {
          return {
            ...tab,
            label: selectedTab === "active" ? "Active" : "Pending"
          };
        }
        return tab;
      })
    );
  };

  const sosCardStatus = (status: SosTabStatus) => {
    switch (status) {
      case 1:
        return {label: "Active", className: "bg-red-500 text-white"};
      case 2:
        return {label: "Resolved", className: "bg-green-500 text-white"};
      case 3:
        return {label: "Cancelled", className: "bg-black text-white"};
      default:
        return {label: "Active", className: "bg-red-500 text-white"};
    }
  };

  const moveUserToStatus = (
    sourceCardId: string,
    userId: string,
    nextStatus: SosTabStatus
  ) => {
    setCards(prevCards => {
      const sourceCard = prevCards.find(card => card.id === sourceCardId);

      if (!sourceCard) {
        return prevCards;
      }

      const movingUser = sourceCard.users.find(user => user.id === userId);

      if (!movingUser) {
        return prevCards;
      }

      const remainingCards = prevCards
        .map(card =>
          card.id === sourceCardId
            ? {...card, users: card.users.filter(user => user.id !== userId)}
            : card
        )
        .filter(card => card.users.length > 0);

      const targetIndex = remainingCards.findIndex(
        card => card.busNo === sourceCard.busNo && card.status === nextStatus
      );

      if (targetIndex >= 0) {
        const updated = [...remainingCards];
        updated[targetIndex] = {
          ...updated[targetIndex],
          users: [...updated[targetIndex].users, movingUser]
        };
        return updated;
      }

      return [
        ...remainingCards,
        {
          ...sourceCard,
          id: `card-${nextStatus}-${sourceCard.busNo.replace(/\s+/g, "-")}`,
          status: nextStatus,
          users: [movingUser]
        }
      ];
    });
  };

  const filterData = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase();
    const fromTime = filterRequest.from;
    const toTime = filterRequest.to;

    return cards.filter(card => {
      const matchesSearch =
        !normalizedQuery ||
        [
          card.busNo,
          card.caretakerName,
          card.driverCode,
          card.from,
          card.to,
          ...card.users.flatMap(user => [user.name, user.bookingId, user.phone])
        ]
          .filter(Boolean)
          .some(value => value.toLowerCase().includes(normalizedQuery));

      const matchesDate = card.users.some(user => {
        const userDate = new Date(user.travelDate).getTime();
        return (
          !Number.isNaN(userDate) &&
          (!hasDateFilterChanged ||
            !fromTime ||
            !toTime ||
            (userDate >= fromTime && userDate <= toTime))
        );
      });

      return matchesSearch && matchesDate;
    });
  }, [
    cards,
    filterRequest.from,
    filterRequest.to,
    hasDateFilterChanged,
    searchValue
  ]);

  const activeCount = filterData.filter(card => card.status === 1).length;
  const resolvedCount = filterData.filter(card => card.status === 2).length;
  const falseAlarmCount = filterData.filter(card => card.status === 3).length;
  const trendChartData = useMemo(() => {
    const dailyMap = new Map<
      string,
      {label: string; active: number; resolved: number; falseAlarm: number}
    >();

    cards.forEach(card => {
      card.users.forEach(user => {
        const date = new Date(user.travelDate);
        if (Number.isNaN(date.getTime())) {
          return;
        }

        const key = user.travelDate;
        const label = new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric"
        }).format(date);

        if (!dailyMap.has(key)) {
          dailyMap.set(key, {label, active: 0, resolved: 0, falseAlarm: 0});
        }

        const current = dailyMap.get(key)!;
        if (card.status === 1) {
          current.active += 1;
        } else if (card.status === 2) {
          current.resolved += 1;
        } else if (card.status === 3) {
          current.falseAlarm += 1;
        }
      });
    });

    const sortedDays = Array.from(dailyMap.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .slice(-7)
      .map(([, value]) => value);

    return {
      categories: sortedDays.map(item => item.label),
      activeSeries: sortedDays.map(item => item.active),
      resolvedSeries: sortedDays.map(item => item.resolved),
      falseAlarmSeries: sortedDays.map(item => item.falseAlarm)
    };
  }, [cards]);

  return (
    <>
      <div className="mb-3 flex flex-row gap-3 ">
        <div className="flex w-[50%] flex-row gap-2">
          <SosStatCard
            label="Active SOS"
            value={activeCount}
            helper="Requires immediate action"
            icon={<FiAlertTriangle className="h-5 w-5 text-rose-600" />}
            iconClass="bg-rose-50"
            valueClass="text-rose-600"
            borderClass="border-rose-200"
          />
          <SosStatCard
            label="Resolved"
            value={resolvedCount}
            helper="Handled and closed"
            icon={<FiCheckCircle className="h-5 w-5 text-emerald-600" />}
            iconClass="bg-emerald-50"
            valueClass="text-emerald-600"
          />
          <SosStatCard
            label="False Alarm"
            value={falseAlarmCount}
            helper="Dismissed after verification"
            icon={<FiSlash className="h-5 w-5 text-slate-600" />}
            iconClass="bg-slate-100"
            valueClass="text-slate-600"
          />
        </div>
        <div className="w-[50%] ">
          <SosStatusChart
            categories={trendChartData.categories}
            activeSeries={trendChartData.activeSeries}
            resolvedSeries={trendChartData.resolvedSeries}
            falseAlarmSeries={trendChartData.falseAlarmSeries}
          />
        </div>
      </div>
      <div className="flex min-h-[calc(100vh-63px)] w-full flex-col p-1">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="w-[500px]">
            <Tabs
              aria-label="Dynamic tabs"
              selectedKey={activeTab}
              variant="underlined"
              onSelectionChange={key => {
                setActiveTab(key as string);
                updateTabLabel(key as string);
              }}
              className="w-full"
              classNames={{
                tabList: "flex w-full",
                tab: "flex-1 text-center text-xs font-semibold ",
                cursor: "!bg-[#2563EB]",
                tabContent:
                  "!text-gray-500 group-data-[selected=true]:!text-[#2563EB]"
              }}
            >
              {tabs.map(item => (
                <Tab key={item.id} title={item.label}></Tab>
              ))}
            </Tabs>
          </div>
        </div>

        {activeTab === "active" && (
          <Active
            sosCards={cards}
            sosCardStatus={sosCardStatus}
            onStatusChange={moveUserToStatus}
            filterData={filterData}
          />
        )}

        {activeTab === "resolved" && (
          <Resolved
            sosCards={cards}
            sosCardStatus={sosCardStatus}
            onStatusChange={moveUserToStatus}
            filterData={filterData}
          />
        )}

        {activeTab === "falseAlarm" && (
          <FalseAlarm
            sosCards={cards}
            sosCardStatus={sosCardStatus}
            onStatusChange={moveUserToStatus}
            filterData={filterData}
          />
        )}
      </div>
    </>
  );
};
